import React, { useState, useEffect } from "react";
import { Flag, Ban, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Modal } from "@/components/ui/modal";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

// Report any post / comment / user. One report per user per item; backend dedupes.
export function ReportDialog({ open, onClose, targetType, targetId, label }) {
  const [reasons, setReasons] = useState([]);
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { api.reportReasons().then(setReasons).catch(() => setReasons([])); setReason(""); setDetails(""); } }, [open]);

  const submit = async () => {
    if (!reason) return;
    setBusy(true);
    try {
      await api.createReport(targetType, targetId, reason, reason === "Other" ? details : "");
      toast("Thanks, we'll review this.");
      onClose();
    } catch (e) { toast(e.message); } finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={onClose} testid="report-dialog">
      <div className="p-5">
        <div className="mb-3 flex items-center gap-2"><Flag className="h-5 w-5 text-aura-magenta" /><h3 className="font-display text-lg font-bold text-white">Report {label || targetType}</h3></div>
        <p className="mb-3 text-sm text-am-text2">Why are you reporting this?</p>
        <div className="grid grid-cols-2 gap-2">
          {reasons.map((r) => (
            <button key={r} onClick={() => setReason(r)} data-testid={`report-reason-${r.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={cn("rounded-xl border px-3 py-2.5 text-left text-sm font-semibold transition-colors", reason === r ? "border-transparent aura-gradient text-black" : "border-am-border bg-am-surface2 text-am-text hover:text-white")}>{r}</button>
          ))}
        </div>
        {reason === "Other" && (
          <textarea value={details} onChange={(e) => setDetails(e.target.value)} maxLength={500} rows={3} data-testid="report-details" placeholder="Tell us more (optional)" className="mt-3 w-full resize-none rounded-xl border border-am-border bg-am-surface2 px-3 py-2.5 text-sm text-white outline-none focus:border-am-border-strong" />
        )}
        <button onClick={submit} disabled={!reason || busy} data-testid="report-submit" className="mt-4 flex w-full items-center justify-center gap-2 rounded-full aura-gradient py-3 text-sm font-bold text-black disabled:opacity-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit report"}</button>
      </div>
    </Modal>
  );
}

export function BlockConfirm({ open, onClose, username, onBlocked }) {
  const [busy, setBusy] = useState(false);
  const block = async () => {
    setBusy(true);
    try { await api.block(username); toast(`Blocked @${username}`); onBlocked && onBlocked(); onClose(); }
    catch (e) { toast(e.message); } finally { setBusy(false); }
  };
  return (
    <Modal open={open} onClose={onClose} testid="block-confirm">
      <div className="p-5">
        <div className="mb-2 flex items-center gap-2"><Ban className="h-5 w-5 text-aura-magenta" /><h3 className="font-display text-lg font-bold text-white">Block @{username}?</h3></div>
        <p className="text-sm text-am-text2">You'll unfollow each other. Neither of you will see the other's posts, comments or profile, and you won't be able to message each other. You can unblock from Settings → Blocked accounts.</p>
        <div className="mt-5 flex gap-2">
          <button onClick={onClose} className="flex-1 rounded-full border border-am-border-strong py-3 text-sm font-semibold text-white hover:bg-white/5">Cancel</button>
          <button onClick={block} disabled={busy} data-testid="block-confirm-btn" className="flex-1 rounded-full bg-aura-magenta py-3 text-sm font-bold text-white disabled:opacity-60">{busy ? "Blocking…" : "Block"}</button>
        </div>
      </div>
    </Modal>
  );
}
